/**
 * Правая панель карточки аукциона (по образцу project-inspector.js: синглтон,
 * стили .task-inspector/.ti-*). Вкладки: Данные, Расчёт, Раскладка, Решение, Этапы.
 * Все изменения идут через эндпоинты /api/tenders/{id}/…; после каждого вызова
 * карточка перечитывается и onChange(tender) обновляет таблицу/kanban.
 *
 *   mountTenderInspector(container);
 *   openTenderInspector(id, { onChange, onClose });
 *   closeTenderInspector();
 */
import { api } from '../api.js';
import { canDo, getCurrentUser } from '../auth.js';
import { attachInspectorResize } from '../utils/inspector-resize.js';
import { openDocViewer } from './doc-viewer.js';
import { openModal } from './modal.js';
import { showNotification } from './notification.js';
import { pickCounterparty } from './ref-picker.js';
import {
  DECISION_LABEL, DECISION_TARGETS, DOC_KIND_LABEL, PREFILTER_SOURCE_LABEL, RECOMMENDATION_LABEL, STAGES,
  STATUS_LABEL, TRANSITIONS, WORK_SCOPE_LABEL, apiErrorText, checkPrice1, eisUrl, esc, meltorUrl, flowIndex,
  fmtBytes, fmtDate, fmtDeadline, fmtMoney, fmtNum, isPrefilterRejected, prefilterBadge, roleAllows, statusText,
  apiErrorDetail, cardName, isOwner, manualAllowed,
} from '../utils/tenders.js';
import {
  REC_WORD, citeText, docsLine, isLegacyScreening, maratReport, missingDocs, num,
} from '../utils/marat-report.js';
import { ERASED_HINT, docForCitation, isErased, isViewable } from '../utils/doc-view.js';
import {
  AGREE_DEVIATION_PCT, GO_REASONS, NO_GO_REASONS, REASON_LABEL, deviationPct, fmtDeviation, reasonRequired, validateDecision,
} from '../utils/decision-feedback.js';

const TABS = [
  { key: 'data',      label: 'Данные' },
  { key: 'estimate',  label: 'Расчёт' },
  { key: 'screening', label: 'Раскладка' },
  { key: 'decision',  label: 'Решение' },
  { key: 'stages',    label: 'Этапы' },
];

const CATEGORY_LABEL = {
  survey: 'Изыскания', design: 'Проектирование', mandatory: 'Обязательные', optional: 'Дополнительно',
};
const OBJECT_KIND_LABEL = { oks: 'ОКС', oks_simple: 'ОКС простой', linear: 'Линейный', capacity: 'По производительности' };

// Справочники общие для всех карточек сессии.
let _rateCard = null;
let _shares = null;

let _root = null;
let _resize = null;
let _st = blank();
// Ц-0: черновик формы решения (цена, категория, комментарий) переживает закрытие и повторное
// открытие той же карточки; сбрасывается при открытии другой карточки и после успешной отправки.
let _draft = null;

function blank() {
  return {
    id: null, tender: null, tab: 'data', onChange: () => {}, onClose: () => {},
    error: null, notice: {}, busy: false, upload: {},
    qty: {}, flags: [], contractPrice: null, estimate: null, objectKind: null,
    // М-1: прецеденты (GET /precedents) и черновик формы решения.
    precedents: null, decide: { price: null, category: '', comment: '' },
    // Ц-1: черновик формы смены цены участия (approved/docs).
    priceEdit: { price: '', category: '', comment: '' },
    // Э-1: черновик формы ведения этапов Динаром.
    stage: blankStage(),
  };
}

function blankStage() {
  return { application_number: '', outcome: 'won', final_price: '', contract_due: '', note: '' };
}

document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape' || !_st.id) return;
  // Esc в открытом окне (просмотр документа, подтверждение) закрывает только окно.
  if (document.querySelector('dialog[open]')) return;
  const t = document.activeElement;
  if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return;
  closeTenderInspector();
});

export function mountTenderInspector(container) {
  if (_root && document.body.contains(_root)) return _root;
  _root = document.createElement('aside');
  _root.className = 'task-inspector tdi-inspector';
  _root.hidden = true;
  _root.innerHTML = `
    <header class="ti-header">
      <span class="ti-title">Аукцион</span>
      <button type="button" class="ti-close" title="Закрыть (Esc)" aria-label="Закрыть">✕</button>
    </header>
    <nav class="tdi-tabs" role="tablist">
      ${TABS.map(t => `<button type="button" role="tab" class="tdi-tab" data-tab="${t.key}">${t.label}</button>`).join('')}
    </nav>
    <div class="ti-body"></div>
    <footer class="ti-footer"></footer>
    <input type="file" multiple hidden data-upload-input accept="${UPLOAD_ACCEPT}">`;
  _root.querySelector('.ti-close').addEventListener('click', closeTenderInspector);
  _root.addEventListener('click', onClick);
  _root.addEventListener('change', onChangeEvt);
  _root.addEventListener('input', onInput);
  _st = blank();
  container.appendChild(_root);
  _resize = attachInspectorResize(_root, { key: 'tender', defaultWidth: 520 });
  return _root;
}

export async function openTenderInspector(id, { onChange, onClose, tab } = {}) {
  if (!_root) return;
  const keepTab = _st.id === id ? _st.tab : null;
  _st = { ...blank(), id, tab: tab ?? keepTab ?? 'data', onChange: onChange ?? (() => {}), onClose: onClose ?? (() => {}) };
  // Ц-0: тот же черновик при повторном открытии карточки (в т.ч. после закрытия); для другой
  // карточки — чистый, привязанный к ней.
  if (_draft && _draft.id === id) _st.decide = _draft.decide;
  else _draft = { id, decide: _st.decide };
  _root.hidden = false;
  void _root.offsetWidth;
  _root.classList.add('ti-open');
  _resize?.notify();
  _root.querySelector('.ti-body').innerHTML = '<div class="loading-spinner">Загрузка карточки...</div>';
  _root.querySelector('.ti-footer').innerHTML = '';
  await reload();
}

export function closeTenderInspector() {
  if (!_root || !_st.id) return;
  const onClose = _st.onClose;
  _st = blank();
  _root.classList.remove('ti-open');
  _root.hidden = true;
  _resize?.notify();
  onClose();
}

export const currentTenderId = () => _st.id;

async function reload() {
  const id = _st.id;
  try {
    const t = await api.tenders.get(id);
    if (_st.id !== id) return;
    _st.tender = t;
    _st.error = null;
    render();
  } catch (e) {
    if (_st.id !== id) return;
    _root.querySelector('.ti-body').innerHTML =
      `<div class="ti-error">${e.status === 404 ? 'Аукцион не найден' : esc(apiErrorText(e))}</div>`;
  }
}

/** Выполнить действие API, перечитать карточку, сообщить наружу. */
async function run(fn, okText, tabNotice) {
  if (_st.busy) return null;
  _st.busy = true;
  _st.error = null;
  render();
  try {
    const res = await fn();
    if (okText) showNotification(okText, 'info');
    if (tabNotice) _st.notice[_st.tab] = tabNotice;
    await reload();
    _st.onChange(_st.tender);
    return res ?? true;
  } catch (e) {
    if (e.message !== '401') {
      _st.error = apiErrorText(e);
      showNotification(_st.error, 'error');
    }
    return null;
  } finally {
    _st.busy = false;
    if (_st.tender) render();
  }
}

// ── Рендер ───────────────────────────────────────────────────────────────────

function render() {
  const t = _st.tender;
  if (!t) return;
  _root.querySelector('.ti-title').innerHTML =
    `<span data-card-title>№${esc(t.id)} · ${esc(t.reg_number)}</span> <span class="tdi-status tdi-status--${esc(t.status)}">${esc(statusText(t))}</span>`;
  _root.querySelectorAll('.tdi-tab').forEach(b => {
    const on = b.dataset.tab === _st.tab;
    b.classList.toggle('is-active', on);
    b.setAttribute('aria-selected', String(on));
  });
  const body = {
    data: renderData, estimate: renderEstimate, screening: renderScreening,
    decision: renderDecision, stages: renderStages,
  }[_st.tab](t);
  const notice = _st.notice[_st.tab];
  _root.querySelector('.ti-body').innerHTML = `
    ${_st.error ? `<div class="ti-error" data-tdi-error>${esc(_st.error)}</div>` : ''}
    ${notice ? `<div class="tdi-notice">${esc(notice)}</div>` : ''}
    <div class="tdi-pane" data-pane="${_st.tab}">${body}</div>`;
  _root.querySelector('.ti-footer').innerHTML = renderFooter(t);
  if (_st.tab === 'estimate' && (!_rateCard || !_shares)) loadRefs();
  if (_st.tab === 'decision' && !_st.precedents) loadPrecedents();
}

async function loadPrecedents() {
  const id = _st.id;
  _st.precedents = { loading: true };
  try {
    const res = await api.tenders.precedents(id);
    if (_st.id !== id) return;
    _st.precedents = res;
  } catch (e) {
    if (_st.id !== id) return;
    _st.precedents = { error: e.message === '401' ? null : apiErrorText(e) };
  }
  if (_st.tab === 'decision') render();
}

const field = (label, value, cls = '') =>
  `<div class="ti-field ${cls}"><span class="ti-label">${esc(label)}</span><span class="ti-value">${value}</span></div>`;
const txt = (v) => (v == null || v === '' ? '—' : esc(v));

/** Связь заказчика аукциона со справочником контрагентов (СПР-1b). */
function customerLinkHtml(t) {
  const writer = canDo('tender.update');
  const status = t.customer_id
    ? '<span class="ref-link-status is-linked">✓ в справочнике</span>'
    : (t.customer_name ? '<span class="ref-link-status is-unlinked">не связан со справочником</span>' : '');
  const btn = writer
    ? `<button type="button" class="ti-btn ti-btn-mini" data-act="link_customer">${t.customer_id ? 'Изменить' : 'Связать'}</button>`
    : '';
  const unlink = writer && t.customer_id
    ? '<button type="button" class="ti-btn ti-btn-mini" data-act="unlink_customer">Отвязать</button>' : '';
  if (!status && !btn) return '';
  return `<div class="ti-field"><span class="ti-label">Справочник</span>
    <span class="ti-value ref-link">${status}${btn}${unlink}</span></div>`;
}

function renderData(t) {
  // У карточек из писем МЭЛТОР eis_url пуст — ведём на поиск ЕИС по номеру.
  const eisHref = eisUrl(t);
  const eis = eisHref
    ? `<a href="${esc(eisHref)}" target="_blank" rel="noopener" class="tdi-link" data-eis>${t.eis_url ? 'Открыть в ЕИС ↗' : 'Открыть в ЕИС (поиск по номеру) ↗'}</a>` : '—';
  const meltorHref = meltorUrl(t);
  const meltor = meltorHref
    ? `<a href="${esc(meltorHref)}" target="_blank" rel="noopener" class="tdi-link" data-meltor
        title="кабинет, поиск по номеру">Открыть в МЭЛТОР ↗</a>`
      + `<br><span class="ti-hint-sm" data-meltor-hint>кабинет, поиск по номеру${t.reg_number ? ` ${esc(t.reg_number)}` : ''}</span>`
    : '—';
  const platform = /^https?:\/\//.test(t.platform ?? '')
    ? `<a href="${esc(t.platform)}" target="_blank" rel="noopener" class="tdi-link">${esc(t.platform)}</a>` : txt(t.platform);
  const contacts = (t.contacts ?? []).map(c => `
    <li>${txt(c.name)}${c.role ? ` · ${esc(c.role)}` : ''}
      ${c.phone ? `<br><a href="tel:${esc(c.phone)}" class="tdi-link">${esc(c.phone)}</a>` : ''}
      ${c.email ? `<br><a href="mailto:${esc(c.email)}" class="tdi-link">${esc(c.email)}</a>` : ''}
      ${c.note ? `<br><span class="ti-hint-sm">${esc(c.note)}</span>` : ''}</li>`).join('');
  return `
    ${renderPrefilter(t)}
    <div class="ti-field"><span class="ti-label">Наименование</span><span class="ti-value ti-value-strong">${txt(t.title)}</span></div>
    ${field('Регион, город', txt([t.region, t.city].filter(Boolean).join(', ') || null))}
    <div class="ti-row2">${field('Закон', txt(t.law))}${field('Способ', txt(t.procurement_type))}</div>
    <div class="ti-row2">${field('НМЦК', fmtMoney(t.initial_price))}${field('Класс работ', txt(t.work_class))}</div>
    <div class="ti-row2">${field('Обеспечение заявки', fmtMoney(t.application_security))}${field('Обеспечение контракта', fmtMoney(t.contract_security))}</div>
    <div class="ti-row2">${field('Начало подачи', fmtDate(t.submission_start, true))}${field('Окончание подачи', fmtDeadline(t))}</div>
    <div class="ti-row2">${field('Дата торгов', fmtDate(t.auction_date, true))}${field('Подведение итогов', fmtDate(t.results_date, true))}</div>
    ${field('Комментарий', t.comment ? esc(t.comment).replace(/\n/g, '<br>') : '—')}
    <h4 class="tdi-h">Заказчик</h4>
    ${field('Наименование', txt(t.customer_name))}
    ${customerLinkHtml(t)}
    <div class="ti-row2">${field('ИНН', txt(t.customer_inn))}${field('КПП', txt(t.customer_kpp))}</div>
    <div class="ti-row2">${field('Регион', txt(t.region))}${field('Город/район', txt(t.city))}</div>
    ${field('Место поставки', txt(t.delivery_place))}
    <div class="ti-row2">${field('Срок исполнения', txt(t.execution_term))}${field('СМП', t.is_smp == null ? '—' : (t.is_smp ? 'да' : 'нет'))}</div>
    <h4 class="tdi-h">Источник</h4>
    <div class="ti-row2">${field('ЕИС', eis)}${field('МЭЛТОР', meltor)}</div>
    ${field('Площадка', platform)}
    <div class="ti-row2">${field('Источник', txt(t.source))}${field('МЭЛТОР ID', txt(t.meltor_id))}</div>
    <div class="ti-row2">${field('Добавлена в МЭЛТОР', fmtDate(t.source_added_at, true))}${field('Ушла из выдачи', fmtDate(t.source_gone_at, true))}</div>
    ${field('Рассылки МЭЛТОР', (t.src_subjects ?? []).length ? esc(t.src_subjects.join(', ')) : '—')}
    ${t.extra_json && Object.keys(t.extra_json).length ? field('Прочие колонки МЭЛТОР',
      Object.entries(t.extra_json).map(([k, v]) => `${esc(k)}: ${esc(v)}`).join('<br>')) : ''}
    <h4 class="tdi-h">Контакты</h4>
    ${contacts ? `<ul class="tdi-list">${contacts}</ul>` : '<p class="ti-hint">Контактов нет.</p>'}
    ${renderDocuments(t)}
    ${renderEnrich(t)}`;
}

// ── Документы и обогащение с сайта МЭЛТОР (Д-1) ──

const TEXT_STATUS_HINT = {
  archive: 'архив', unsupported: 'текст не извлечён', error: 'ошибка чтения',
  empty: 'без текста', too_large: 'больше лимита, только ссылка', skipped: 'не контракт и не ТЗ',
};

const UPLOAD_ACCEPT = '.docx,.doc,.pdf,.xlsx,.xls,.zip,.7z,.rar';
const MAX_UPLOAD_FILE = 25 * 1024 * 1024;     // лимиты POST /documents/upload (Х-2)
const MAX_UPLOAD_TOTAL = 100 * 1024 * 1024;
const UPLOAD_KIND_LABEL = { contract: 'договор', tz: 'ТЗ', nmck: 'обоснование НМЦК' };
const KEEP_KINDS = new Set(['contract', 'calendar_plan', 'tz', 'nmck']);

function docItem(t, d, owner) {
  // Ссылки на файлы сайта МЭЛТОР не показываем: без входа в кабинет они не открываются (UI-3).
  const name = txt(d.filename || d.url);
  const open = isViewable(d) ? ` <button type="button" class="ti-btn ti-btn-mini" data-doc-open="${d.id}">Открыть</button>` : '';
  let status = d.note || TEXT_STATUS_HINT[d.text_status];
  if (isErased(d)) status = ERASED_HINT;
  else if (KEEP_KINDS.has(d.doc_kind) && !d.has_file && d.doc_type === 'manual') {
    status = 'загрузите файл заново, чтобы открыть его в приложении';
  }
  const meta = [DOC_KIND_LABEL[d.doc_kind] ?? d.doc_kind, fmtBytes(d.file_size), status]
    .filter(Boolean).map(esc).join(' · ');
  const del = owner ? `<button type="button" class="tdi-doc-del" data-doc-del="${d.id}" title="Удалить документ"
    aria-label="Удалить документ"${_st.busy ? ' disabled' : ''}>✕</button>` : '';
  return `<li class="${d.parent_id ? 'tdi-doc-child' : ''}" data-doc="${d.id}">${d.parent_id ? '↳ ' : ''}<span data-doc-name>${name}</span>${open}${del}
    ${meta ? `<br><span class="ti-hint-sm"${isErased(d) ? ' data-doc-erased' : ''}>${meta}</span>` : ''}</li>`;
}

function renderDocuments(t) {
  const docs = t.documents ?? [];
  const upload = uploadControl(t);
  if (!docs.length) {
    return `<h4 class="tdi-h">Документы</h4><p class="ti-hint" data-docs-empty>Документов пока нет.</p>${upload}`;
  }
  const owner = getCurrentUser()?.role === 'owner';
  const kept = docs.filter(d => d.doc_kind !== 'skipped');
  const skipped = docs.filter(d => d.doc_kind === 'skipped');
  const keptFiles = kept.filter(d => d.text_status !== 'archive').length;
  return `<h4 class="tdi-h">Документы (${keptFiles} из ${docs.filter(d => d.text_status !== 'archive').length})</h4>
    <p class="ti-hint-sm">Договор, ТЗ и обоснование НМЦК открываются здесь до окончания срока подачи; остальное — в ЕИС.</p>
    <ul class="tdi-list" data-docs>${kept.map(d => docItem(t, d, owner)).join('')}</ul>
    ${skipped.length ? `<details class="tdi-skipped" data-docs-skipped><summary>Пропущено: ${skipped.length}</summary>
      <ul class="tdi-list">${skipped.map(d => docItem(t, d, owner)).join('')}</ul></details>` : ''}
    ${upload}`;
}

// ── Ручная загрузка договора и ТЗ (POST /documents/upload, Х-2) ──

function uploadControl(t) {
  if (!canDo('tender.update') || t.is_closed) return '';
  const u = _st.upload;
  return `<div class="tdi-upload" data-upload>
      <button type="button" class="ti-btn" data-act="upload"${_st.busy || u.busy ? ' disabled' : ''}>${u.busy ? 'Загрузка…' : 'Загрузить документы'}</button>
      <span class="ti-hint-sm">договор, ТЗ, обоснование НМЦК: docx, doc, pdf, xlsx или архив; до 25 МБ на файл</span>
      ${renderUploadStatus()}
    </div>`;
}

function renderUploadStatus() {
  const u = _st.upload;
  if (u.result) {
    const accepted = u.result.accepted ?? [];
    const skipped = u.result.skipped ?? [];
    const row = (d) => `<li>${esc(d.filename ?? '—')} <span class="ti-hint-sm">${esc(DOC_KIND_LABEL[d.doc_kind] ?? d.doc_kind ?? '')}${d.note ? ` · ${esc(d.note)}` : ''}</span></li>`;
    return `<div class="tdi-upload-res" data-upload-result>
        <div>Принято: ${accepted.length}</div>${accepted.length ? `<ul class="tdi-list">${accepted.map(row).join('')}</ul>` : ''}
        ${skipped.length ? `<details data-upload-skipped><summary>Пропущено: ${skipped.length}</summary>
          <ul class="tdi-list">${skipped.map(row).join('')}</ul></details>` : ''}
        <div class="tdi-upload-queued" data-upload-queued>Пересчёт раскладки в очереди: раннер возьмёт карточку в течение нескольких минут.</div>
      </div>`;
  }
  if (u.unrecognized) {
    const files = u.unrecognized.files ?? [];
    return `<div class="ti-error tdi-upload-422" data-upload-422>
        <b>Не распознан ни договор, ни ТЗ, ни обоснование НМЦК.</b> Ничего не сохранено. Укажите тип файлов и отправьте ещё раз.
        ${files.length ? `<ul class="tdi-list">${files.map(f => `<li>${esc(f.filename)} <span class="ti-hint-sm">${esc(DOC_KIND_LABEL[f.doc_kind] ?? f.doc_kind ?? '')}</span></li>`).join('')}</ul>` : ''}
        <div class="tdi-upload-kinds">
          <button type="button" class="ti-btn" data-act="upload-as" data-kind="contract">Это договор</button>
          <button type="button" class="ti-btn" data-act="upload-as" data-kind="tz">Это ТЗ</button>
          <button type="button" class="ti-btn" data-act="upload-as" data-kind="nmck">Это обоснование НМЦК</button>
        </div>
      </div>`;
  }
  if (u.error) return `<div class="ti-error" data-upload-error>${esc(u.error)}</div>`;
  return '';
}

async function uploadFiles(files, docKind) {
  const t = _st.tender;
  if (!t || !files.length || _st.upload.busy) return;
  const big = files.find(f => f.size > MAX_UPLOAD_FILE);
  const total = files.reduce((n, f) => n + f.size, 0);
  if (big || total > MAX_UPLOAD_TOTAL) {
    _st.upload = { error: big ? `Файл «${big.name}» больше 25 МБ` : 'Файлы вместе больше 100 МБ' };
    render();
    return;
  }
  _st.upload = { busy: true };
  render();
  try {
    const res = await api.tenders.uploadDocuments(t.id, files, docKind);
    _st.upload = { result: res };
    const kind = docKind ? ` как ${UPLOAD_KIND_LABEL[docKind]}` : '';
    showNotification(`Документы загружены${kind}: пересчёт раскладки в очереди`, 'info');
    await reload();
    _st.onChange(_st.tender);
  } catch (e) {
    const d = apiErrorDetail(e);
    if (e.message === '401') _st.upload = {};
    else if (e.status === 422 && d?.error === 'no_contract_or_tz') _st.upload = { unrecognized: d, pending: files };
    else _st.upload = { error: e.status === 413 ? `Слишком большой файл: ${apiErrorText(e)}` : apiErrorText(e) };
  } finally {
    if (_st.tender) render();
  }
}

async function deleteDocument(docId) {
  const t = _st.tender;
  const d = (t.documents ?? []).find(x => x.id === docId);
  const archive = d?.text_status === 'archive';
  const ok = await openModal({
    title: 'Удалить документ', danger: true, confirmLabel: 'Удалить',
    message: `«${d?.filename ?? d?.url ?? docId}» будет удалён${archive ? ' вместе с файлами архива' : ''}.`,
  });
  if (!ok) return;
  await run(() => api.tenders.deleteDocument(t.id, docId), 'Документ удалён');
}

function renderEnrich(t) {
  const state = t.enrich_requested_at ? `запрошено ${fmtDate(t.enrich_requested_at, true)}, worker возьмёт на следующем проходе`
    : t.enrich_attempted_at ? `обновлено ${fmtDate(t.enrich_attempted_at, true)}` : 'ещё не обогащалась';
  // Обогащение запускает только владелец; для остальных оно автоматическое (UI-2, п.8).
  const can = isOwner() && !t.is_closed && t.prefilter_result !== 'reject';
  return `<h4 class="tdi-h">Сайт МЭЛТОР</h4>
    <p class="ti-hint" data-enrich-state>Точные сроки и документы: ${esc(state)}.</p>
    ${can ? `<div><button type="button" class="ti-btn" data-act="enrich"${_st.busy || t.enrich_requested_at ? ' disabled' : ''}>Запросить обогащение</button></div>` : ''}`;
}

// ── Первичный отбор (префильтр Руслана, Ф-1) ──

/** Отбор можно поменять вручную у новых карточек и у отсеянных префильтром. */
const inPrefilter = (t) => (t.status === 'new' && !t.is_closed) || isPrefilterRejected(t);

function renderPrefilter(t) {
  const b = prefilterBadge(t);
  const label = { pass: 'Пропущен', reject: 'Отсеян', review: 'Проверить' }[t.prefilter_result];
  const state = b ? `<span class="tdi-pill tdi-pill--${b.cls}">${esc(label ?? t.prefilter_result)}</span>`
    : '<span class="ti-hint-sm">ещё не оценена</span>';
  if (!isOwner()) return '';   // отбор видит только владелец (UI-2, п.8)
  const btns = canDo('tender.prefilter') && inPrefilter(t) ? `
    <div class="tdi-prefilter-btns">
      ${t.prefilter_result !== 'pass' || isPrefilterRejected(t) ? `<button type="button" class="ti-btn ti-btn-primary" data-act="pf_pass"${_st.busy ? ' disabled' : ''}>Вернуть в работу</button>` : ''}
      ${!isPrefilterRejected(t) ? `<button type="button" class="ti-btn ti-btn-danger" data-act="pf_reject"${_st.busy ? ' disabled' : ''}>Отсеять</button>` : ''}
    </div>` : '';
  return `
    <section class="tdi-prefilter" data-prefilter>
      <h4 class="tdi-h">Отбор</h4>
      <div class="ti-row2">${field('Итог', state)}${field('Состав работ', txt(WORK_SCOPE_LABEL[t.work_scope] ?? t.work_scope))}</div>
      ${t.prefilter_reason ? field('Причина', esc(t.prefilter_reason)) : ''}
      ${t.prefilter_result ? `<div class="ti-row2">${field('Источник', txt(PREFILTER_SOURCE_LABEL[t.prefilter_source] ?? t.prefilter_source))}${field('Версия правил', txt(t.prefilter_rule_version))}</div>
        ${field('Когда', fmtDate(t.prefilter_at, true))}` : ''}
      ${btns}
    </section>`;
}

async function prefilterOverride(result) {
  const t = _st.tender;
  const pass = result === 'pass';
  const v = await openModal({
    title: pass ? 'Вернуть в работу' : 'Отсеять закупку',
    danger: !pass,
    confirmLabel: pass ? 'Вернуть в работу' : 'Отсеять',
    message: 'Комментарий попадёт в примеры для автоматического отбора.',
    fields: [{ name: 'comment', label: 'Комментарий', placeholder: pass ? 'почему закупка нам подходит' : 'почему не подходит' }],
  });
  if (!v) return;
  await run(
    () => api.tenders.prefilterOverride(t.id, { result, comment: v.comment?.trim() || undefined }),
    pass ? 'Возвращена в работу' : 'Отсеяна',
  );
}

// ── Расчёт ──

async function loadRefs() {
  try {
    [_rateCard, _shares] = await Promise.all([
      _rateCard ?? api.tenders.rateCard(),
      _shares ?? api.tenders.sectionShares(),
    ]);
  } catch (e) {
    _st.error = `Справочники расценок не загрузились: ${apiErrorText(e)}`;
    _rateCard = _rateCard ?? [];
    _shares = _shares ?? [];
  }
  if (_st.tender && _st.tab === 'estimate') render();
}

function estimateLines(t) {
  return _st.estimate?.lines ?? t.estimates ?? [];
}

function qtyOf(code, t) {
  if (code in _st.qty) return _st.qty[code];
  const ln = (t.estimates ?? []).find(l => l.item_code === code);
  return ln ? fmtNum(ln.qty) : '';
}

function renderEstimate(t) {
  if (!_rateCard || !_shares) return '<div class="loading-spinner">Загрузка расценок...</div>';
  const editable = canDo('tender.estimate');
  const lines = Object.fromEntries(estimateLines(t).map(l => [l.item_code, l]));
  const flagsAll = [...new Set(_rateCard.flatMap(r => Object.keys(r.region_coeffs ?? {})))];
  let lastCat = null;
  const rows = _rateCard.map(r => {
    const head = r.category !== lastCat
      ? `<tr class="tdi-cat"><td colspan="4">${esc(CATEGORY_LABEL[r.category] ?? r.category)}</td></tr>` : '';
    lastCat = r.category;
    const ln = lines[r.item_code];
    const price = r.calc === 'per_unit' ? fmtMoney(r.unit_price)
      : r.calc === 'pct_of_design' ? `${fmtNum(r.pct)}% ПД` : `≤ ${fmtMoney(r.unit_price)} / ${fmtNum(r.pct)}%`;
    const q = qtyOf(r.item_code, t);
    return `${head}<tr${ln ? ' class="has-amount"' : ''}>
      <td title="${esc(r.applies_when ?? '')}">${esc(r.title)}<div class="tdi-code">${esc(r.item_code)}</div></td>
      <td class="tdi-qty-cell">${editable
        ? `<input type="number" min="0" step="any" class="ti-input tdi-qty" data-qty="${esc(r.item_code)}" value="${esc(q)}">`
        : esc(q || '—')} <span class="ti-hint-sm">${esc(r.unit ?? '')}</span></td>
      <td class="num">${price}</td>
      <td class="num">${ln ? fmtMoney(ln.amount) : ''}</td></tr>`;
  }).join('');

  const total = _st.estimate?.total ?? t.estimated_cost;
  const byCat = _st.estimate?.by_category ?? estimateLines(t).reduce((acc, l) => {
    if (l.category) acc[l.category] = (acc[l.category] ?? 0) + Number(l.amount);
    return acc;
  }, {});
  const design = Number(byCat.design ?? 0);
  const kind = _st.objectKind ?? (estimateLines(t).some(l => /linear/.test(l.item_code)) ? 'linear' : 'oks');
  const shares = _shares.filter(s => s.object_kind === kind);

  return `
    <div class="ti-row2">
      <div class="ti-field"><label class="ti-label" for="tdi-contract-price">Цена контракта для экспертизы</label>
        <input id="tdi-contract-price" type="number" min="0" step="any" class="ti-input" data-contract-price
          value="${esc(_st.contractPrice ?? fmtNum(t.initial_price))}"${editable ? '' : ' disabled'}></div>
      <div class="ti-field"><span class="ti-label">Коэффициенты</span>
        ${flagsAll.map(f => `<label class="ti-checkbox"><input type="checkbox" data-flag="${esc(f)}"${_st.flags.includes(f) ? ' checked' : ''}${editable ? '' : ' disabled'}> ${esc(f)}</label>`).join('') || '—'}</div>
    </div>
    <div class="tdi-table-wrap"><table class="tdi-table">
      <thead><tr><th>Позиция</th><th>Объём</th><th class="num">Цена</th><th class="num">Сумма</th></tr></thead>
      <tbody>${rows}</tbody>
    </table></div>
    <div class="tdi-total">
      <span>Итого расчётная стоимость</span><strong data-est-total>${fmtMoney(total)}</strong>
    </div>
    <div class="tdi-bycat">${Object.entries(byCat).map(([k, v]) =>
      `<span>${esc(CATEGORY_LABEL[k] ?? k)}: <b>${fmtMoney(v)}</b></span>`).join('')}</div>
    ${editable ? `<div><button type="button" class="ti-btn ti-btn-primary" data-act="estimate"${_st.busy ? ' disabled' : ''}>Пересчитать</button>
      <span class="ti-hint-sm">Считаются позиции с объёмом больше нуля.</span></div>` : ''}
    <h4 class="tdi-h">Разбивка ПД по разделам</h4>
    <div class="ti-field"><select class="ti-input" data-object-kind>
      ${Object.entries(OBJECT_KIND_LABEL).map(([k, l]) => `<option value="${k}"${k === kind ? ' selected' : ''}>${l}</option>`).join('')}
    </select></div>
    ${design > 0 ? `<table class="tdi-table tdi-shares"><tbody>
      ${shares.map(s => `<tr><td class="tdi-code">${esc(s.section_code)}</td><td>${esc(s.title)}</td>
        <td class="num">${fmtNum(s.share_pct)}%</td><td class="num">${fmtMoney(design * Number(s.share_pct) / 100)}</td></tr>`).join('')}
    </tbody></table>` : '<p class="ti-hint">Разбивка появится, когда в расчёте будет проектирование.</p>'}`;
}

function estimatePayload() {
  const items = _rateCard
    .map(r => ({ item_code: r.item_code, qty: Number(qtyOf(r.item_code, _st.tender)) }))
    .filter(i => Number.isFinite(i.qty) && i.qty > 0);
  const body = { items, region_flags: _st.flags };
  const cp = _st.contractPrice ?? fmtNum(_st.tender.initial_price);
  if (cp !== '' && cp != null) body.contract_price = Number(cp);
  return body;
}

// ── Раскладка: формат Марата (marat_report, Х-2), сырые данные — в «Технических данных» ──

const TASK_TITLES = [
  'Задание №1. Данные из контракта и технического задания',
  'Задание №2. Оценка стоимости работ',
  'Задание №3. Оценка рисков',
  'Задание №4. Стоимость по разделам',
];

function renderValue(v, depth = 0) {
  if (v == null || v === '') return '—';
  if (Array.isArray(v)) {
    if (!v.length) return '—';
    return `<ul class="tdi-list">${v.map(x => `<li>${renderValue(x, depth + 1)}</li>`).join('')}</ul>`;
  }
  if (typeof v === 'object') {
    if (depth > 3) return `<code>${esc(JSON.stringify(v))}</code>`;
    return `<dl class="tdi-dl">${Object.entries(v).map(([k, x]) =>
      `<dt>${esc(k)}</dt><dd>${renderValue(x, depth + 1)}</dd>`).join('')}</dl>`;
  }
  if (typeof v === 'boolean') return v ? 'да' : 'нет';
  return esc(v);
}

/** «(документ, пункт · открыть)»: ссылка ведёт в окно просмотра на документ цитаты (UI-3). */
function cite(c, doc) {
  if (!c) return '';
  const open = doc ? ` · <button type="button" class="tdi-cite-open" data-doc-open="${doc.id}"
    title="${esc(doc.filename ?? '')}">открыть</button>` : '';
  return ` <span class="tdi-cite">(${esc(c)}${open})</span>`;
}

/** «Название пункта: значение (документ, пункт)»; не найденное — серым. */
function mline(title, value, { citation, note, found = true, cls = '', doc = null } = {}) {
  return `<p class="tdi-mline${found ? '' : ' is-missing'}${cls ? ` ${cls}` : ''}"><b>${esc(title)}:</b> ${esc(value)}${cite(citation, doc)}${note ? ` <span class="tdi-cite">— ${esc(note)}</span>` : ''}</p>`;
}

const citeDoc = (c) => docForCitation(_st.tender, c);
const itemLine = (it, n) => mline(n ? `${it.n}. ${it.title}` : it.title, it.value, { ...it, doc: citeDoc(it.citation) });

function task2Html(t2, complete) {
  const rows = (t2.items ?? []).map(i => {
    const calc = [`${num(i.qty) ?? '—'} ${i.unit ?? ''}`.trim(), i.unit_price != null ? fmtMoney(i.unit_price) : null,
      i.coeff != null && Number(i.coeff) !== 1 ? `коэф. ${num(i.coeff)}` : null].filter(Boolean).join(' × ');
    return mline(i.title, fmtMoney(i.amount), { citation: calc, note: i.note });
  }).join('');
  const total = complete && t2.total != null
    ? `<p class="tdi-mtotal" data-task2-total>Итого стоимость работ: ${fmtMoney(t2.total)}</p>`
    : '<p class="tdi-mtotal" data-task2-total>Итого: расчёта нет — нужны договор и ТЗ</p>';
  const notes = (t2.notes ?? []).map(x => `<p class="ti-hint-sm">${esc(x)}</p>`).join('');
  return `${rows}${total}${notes}`;
}

function task3Html(t3) {
  const disc = (t3.discrepancies ?? []).map(d => mline(d.critical ? 'Разночтение (критичное)' : 'Разночтение', d.text,
    { citation: citeText(d.citation), cls: d.critical ? 'is-critical' : '', doc: citeDoc(d.citation) })).join('')
    || mline('Разночтения договора и ТЗ', 'не найдены');
  const cust = t3.customer ?? {};
  const who = [cust.name, cust.inn ? `ИНН ${cust.inn}` : null].filter(Boolean).join(', ') || '—';
  return `${disc}
    ${['penalty_per_day', 'extension_possible', 'price_increase_possible'].map(k => (t3[k] ? itemLine(t3[k]) : '')).join('')}
    ${mline('Заказчик', who, { note: cust.assessment })}`;
}

function task4Html(t4, complete) {
  const kind = OBJECT_KIND_LABEL[t4.object_kind] ?? t4.object_kind;
  const head = mline('Тип объекта', kind || '—') + (complete && t4.design_total != null
    ? mline('Стоимость проектирования', fmtMoney(t4.design_total)) : '');
  const rows = (t4.sections ?? []).map(s => mline(`${s.title} (${num(s.share_pct)} %)`, fmtMoney(s.amount))).join('');
  return head + (rows || '<p class="ti-hint">Разбивка появится, когда в расчёте будет проектирование.</p>');
}

function recChips(s) {
  const r = s.recommendation ?? {};
  const chips = [
    ...(r.stop_factors ?? []).map(x => `<span class="tdi-chip tdi-chip--stop">${esc(x)}</span>`),
    ...(r.warnings ?? []).map(x => `<span class="tdi-chip tdi-chip--warn">${esc(x)}</span>`),
  ];
  return chips.length ? `<div class="tdi-chips" data-rec-chips>${chips.join('')}</div>` : '';
}

const priceCell = (label, v, strong = false) =>
  `<div class="tdi-price"><span class="ti-label">${esc(label)}</span><span class="${strong ? 'tdi-price-main' : ''}">${fmtMoney(v)}</span></div>`;

function renderScreening(t) {
  const s = t.screening_json;
  // Раскладку запускает только владелец; для остальных она автоматическая (UI-2, п.8).
  const canRun = isOwner() && canDo('tender.estimate');
  const btn = (label, primary) => (canRun
    ? `<button type="button" class="ti-btn${primary ? ' ti-btn-primary' : ''}" data-act="screen"${_st.busy ? ' disabled' : ''}>${label}</button>` : '');
  if (!s) {
    return `<p class="ti-hint">Раскладки ещё нет: извлечение объёмов и рисков из договора и ТЗ, расчёт,
      проверка заказчика и мощности, рекомендация и цена.</p>${uploadControl(t)}<div>${btn('Сделать раскладку', true)}</div>`;
  }
  const tech = `<details class="tdi-block" data-tech><summary>Технические данные</summary>
    <div class="tdi-block-body">${renderValue(Object.fromEntries(Object.entries(s).filter(([k]) => k !== 'marat_report')))}</div></details>`;
  if (isLegacyScreening(s)) {
    return `<div class="tdi-legacy" data-legacy>
        <b>Раскладка старого формата.</b> Отчёт по заданиям Марата появится после повторной раскладки
        (${esc(fmtDate(t.screened_at, true))}).${canRun ? '' : ' Раскладка обновится автоматически.'}
        <div>${btn('Разложить заново', true)}</div>
      </div>${tech}`;
  }
  const r = maratReport(s);
  const rec = t.recommendation ?? s.recommendation?.value;
  const pp = r.price_proposal ?? {};
  const complete = r.complete !== false && rec !== 'incomplete';
  const docs = docsLine(s);
  const card = `
    <section class="tdi-rec-card tdi-rec--${esc(rec ?? 'none')}" data-rec-card>
      <div class="tdi-rec-head"><span class="tdi-rec-word" data-rec-word>${esc(REC_WORD[rec] ?? RECOMMENDATION_LABEL[rec] ?? 'Без рекомендации')}</span>
        <span class="ti-hint-sm">разложено ${fmtDate(t.screened_at, true)}</span></div>
      ${complete ? `<div class="tdi-prices">
          ${priceCell('Рекомендуемая цена', pp.recommended_price ?? t.recommended_price, true)}
          ${priceCell('Нижняя граница', pp.floor)}
          ${priceCell('Расчётная стоимость', t.estimated_cost ?? r.task2?.total)}
          ${priceCell('НМЦК', pp.nmck ?? t.initial_price)}
        </div>
        ${pp.reason ? `<p class="tdi-rec-reason" data-rec-reason>${esc(pp.reason)}</p>` : ''}`
      : `<div class="tdi-need-docs" data-need-docs>Нужны документы: ${esc(missingDocs(s).join(', '))}</div>${uploadControl(t)}`}
      ${recChips(s)}
      ${docs ? `<p class="ti-hint-sm" data-docs-line>${esc(docs)}</p>` : ''}
    </section>`;
  const tasks = [
    (r.task1 ?? []).map(it => itemLine(it, true)).join(''),
    task2Html(r.task2 ?? {}, complete),
    task3Html(r.task3 ?? {}),
    task4Html(r.task4 ?? {}, complete),
  ];
  return `${card}
    ${tasks.map((html, i) => `<section class="tdi-marat" data-task="${i + 1}"><h4 class="tdi-h">${TASK_TITLES[i]}</h4>${html}</section>`).join('')}
    ${tech}
    <div>${btn('Разложить заново')}</div>`;
}

// ── Решение (screened и decision: бэкенд принимает оба, Х-1) ──

const DECIDE_FROM = new Set(['screened', 'decision']);

function aiNoGo(t) {
  // К-1: no_go уходит в decision; баннер показываем в decision (и screened — старые карточки).
  if (!DECIDE_FROM.has(t.status) || t.recommendation !== 'no_go') return '';
  const r = t.screening_json?.recommendation ?? {};
  const why = (r.stop_factors?.length ? r.stop_factors : r.warnings ?? []).join('; ')
    || t.screening_json?.marat_report?.price_proposal?.reason || '';
  return `<div class="tdi-ai-nogo" data-ai-nogo><b>ИИ рекомендует отказ</b>${why ? `<br>${esc(why)}` : ''}</div>`;
}

const reasonLabel = (code) => REASON_LABEL[code] ?? code ?? '';


function decideHint(t) {
  const dev = deviationPct(_st.decide.price, t.recommended_price);
  const why = reasonRequired('go', dev, t.recommendation);
  return why
    ? `Участие: нужны категория и комментарий — ${why}. Отказ: всегда с категорией и комментарием.`
    : 'Участие: согласие с рекомендацией запишется само, категория и комментарий по желанию. Отказ: с категорией и комментарием.';
}

function deviationText(t) {
  const dev = deviationPct(_st.decide.price, t.recommended_price);
  if (dev == null) return t.recommended_price == null ? 'рекомендованной цены нет' : '';
  return `${fmtDeviation(dev)} к рекомендованной`;
}

/** Ц-0: живой показ введённой цены с разрядами («1 250 000 ₽»); пусто/не число — ''. */
function priceSumText(raw) {
  if (raw == null || String(raw).trim() === '') return '';
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? fmtMoney(n) : '';
}

// ── Ц-1: цена участия после решения (approved/docs) ──

const PRICE_EDIT_FROM = new Set(['approved', 'docs']);

function deadlinePassed(t) {
  return t.submission_deadline != null && new Date(t.submission_deadline).getTime() <= Date.now();
}

function priceDeviationText(t) {
  const dev = deviationPct(_st.priceEdit.price, t.recommended_price);
  if (dev == null) return t.recommended_price == null ? 'рекомендованной цены нет' : '';
  return `${fmtDeviation(dev)} к рекомендованной`;
}

function priceHint(t) {
  const dev = deviationPct(_st.priceEdit.price, t.recommended_price);
  if (dev != null && Math.abs(dev) > AGREE_DEVIATION_PCT) {
    return `Отклонение ${fmtDeviation(dev)} от рекомендованной — укажите категорию причины.`;
  }
  return 'Категория и комментарий по желанию; при отклонении больше 10 % категория обязательна.';
}

function renderPriceHistory() {
  const h = _st.tender?.price_history ?? [];
  if (!h.length) return '<p class="ti-hint-sm">Изменений цены пока не было.</p>';
  const rows = h.map(x => `<li class="tdi-price-h">
      <div class="ti-hint-sm">${fmtDate(x.changed_at, true)}${x.changed_by ? ` · ${esc(x.changed_by)}` : ''}</div>
      <div><b>${fmtMoney(x.old_price)} → ${fmtMoney(x.new_price)}</b>${x.reason_category ? ` <span class="tdi-chip">${esc(reasonLabel(x.reason_category))}</span>` : ''}</div>
      ${x.comment ? `<div class="ti-hint-sm">${esc(x.comment)}</div>` : ''}
    </li>`).join('');
  return `<ul class="tdi-price-hist" data-price-history>${rows}</ul>`;
}

function renderPriceSection(t) {
  if (!PRICE_EDIT_FROM.has(t.status)) return '';
  let form;
  if (!canDo('tender.decide')) {
    form = '<p class="ti-hint-sm">Цену участия меняет Марат или владелец. Здесь только история.</p>';
  } else if (deadlinePassed(t)) {
    form = '<p class="ti-hint-sm">Срок подачи прошёл — цену участия менять нельзя.</p>';
  } else {
    const d = _st.priceEdit;
    const opt = (c) => `<option value="${c}"${d.category === c ? ' selected' : ''}>${esc(reasonLabel(c))}</option>`;
    const recHint = t.recommended_price != null
      ? `<span class="ti-hint-sm" data-price-rec>Рекомендуемая: ${fmtMoney(t.recommended_price)}</span>` : '';
    form = `
      <div class="tdi-price-edit" data-price-form>
        <div class="ti-field"><label class="ti-label" for="tdi-price-new">Цена участия, ₽</label>
          <div class="tdi-price-row">
            <input id="tdi-price-new" type="number" min="0" step="any" class="ti-input" data-price-input
              value="${esc(d.price)}" placeholder="новая цена участия вручную">
            <span class="tdi-deviation" data-price-deviation>${esc(priceDeviationText(t))}</span>
          </div>
          <div class="tdi-price-live"><span class="ti-hint-sm" data-price-sum>${esc(priceSumText(d.price))}</span>${recHint}</div></div>
        <div class="ti-field"><label class="ti-label" for="tdi-price-reason">Категория причины</label>
          <select id="tdi-price-reason" class="ti-input" data-price-reason>
            <option value=""${d.category ? '' : ' selected'}>— не выбрана —</option>
            ${GO_REASONS.map(opt).join('')}
          </select></div>
        <div class="ti-field"><label class="ti-label" for="tdi-price-comment">Комментарий к изменению</label>
          <textarea id="tdi-price-comment" rows="2" class="ti-input" data-price-comment
            placeholder="почему меняем цену">${esc(d.comment)}</textarea></div>
        <p class="ti-hint-sm" data-price-hint>${esc(priceHint(t))}</p>
        <div class="tdi-decide-btns">
          <button type="button" class="ti-btn ti-btn-primary" data-act="save_price"${_st.busy ? ' disabled' : ''}>Сохранить цену</button>
        </div>
      </div>`;
  }
  return `<h4 class="tdi-h">Цена участия</h4>${form}
    <div class="tdi-price-hist-wrap"><h5 class="tdi-h5">История цены</h5>${renderPriceHistory()}</div>`;
}

function renderDecision(t) {
  const cur = _st.precedents?.current;
  const info = t.decision ? `
    <div class="ti-row2">${field('Решение', esc(DECISION_LABEL[t.decision] ?? t.decision))}${field('Цена участия', fmtMoney(t.price_1))}</div>
    <div class="ti-row2">${field('Кто', txt(t.decision_by))}${field('Когда', fmtDate(t.decision_at, true))}</div>
    ${cur ? `<div class="ti-row2" data-decision-reason>${field('Категория', esc(reasonLabel(cur.reason_category)))}${field('Отклонение', cur.deviation_pct != null ? esc(fmtDeviation(Number(cur.deviation_pct))) : '—')}</div>` : ''}
    ${t.decision_comment ? field('Комментарий', esc(t.decision_comment)) : ''}` : '<p class="ti-hint">Решения пока нет.</p>';
  const summary = `
    <div class="ti-row3">${field('НМЦК', fmtMoney(t.initial_price))}${field('Расчётная', fmtMoney(t.estimated_cost))}${field('Рекомендуемая', fmtMoney(t.recommended_price))}</div>
    ${t.recommendation ? field('Рекомендация раскладки', esc(RECOMMENDATION_LABEL[t.recommendation] ?? t.recommendation)) : ''}`;
  let form = '';
  if (canDo('tender.decide')) {
    if (DECIDE_FROM.has(t.status)) {
      // Ц-0: поле цены всегда начинается пустым; рекомендованная цена — только подсказка,
      // в поле и в решение не подставляется.
      if (_st.decide.price == null) _st.decide.price = '';
      const d = _st.decide;
      const opt = (c) => `<option value="${c}"${d.category === c ? ' selected' : ''}>${esc(reasonLabel(c))}</option>`;
      const recHint = t.recommended_price != null
        ? `<span class="ti-hint-sm" data-price1-rec>Рекомендуемая: ${fmtMoney(t.recommended_price)}</span>` : '';
      form = `
      ${aiNoGo(t)}
      <div class="tdi-decide" data-decide-form>
        <div class="ti-field"><label class="ti-label" for="tdi-price1">Цена участия, ₽</label>
          <div class="tdi-price-row">
            <input id="tdi-price1" type="number" min="0" step="any" class="ti-input" data-price1
              value="${esc(d.price)}" placeholder="наша цена участия вручную">
            <span class="tdi-deviation" data-deviation>${esc(deviationText(t))}</span>
          </div>
          <div class="tdi-price-live"><span class="ti-hint-sm" data-price1-sum>${esc(priceSumText(d.price))}</span>${recHint}</div></div>
        <div class="ti-field"><label class="ti-label" for="tdi-reason">Категория причины</label>
          <select id="tdi-reason" class="ti-input" data-reason>
            <option value=""${d.category ? '' : ' selected'}>— не выбрана —</option>
            <optgroup label="Участие">${GO_REASONS.map(opt).join('')}</optgroup>
            <optgroup label="Отказ">${NO_GO_REASONS.filter(c => c !== 'other').map(opt).join('')}</optgroup>
          </select></div>
        <div class="ti-field"><label class="ti-label" for="tdi-comment">Комментарий</label>
          <textarea id="tdi-comment" rows="3" class="ti-input" data-comment
            placeholder="почему так: объёмы, риски, заказчик, конкуренция">${esc(d.comment)}</textarea></div>
        <p class="ti-hint-sm" data-reason-hint>${esc(decideHint(t))}</p>
        <div class="tdi-decide-btns">
          <button type="button" class="ti-btn ti-btn-primary" data-act="go"${_st.busy ? ' disabled' : ''}>Участвуем</button>
          <button type="button" class="ti-btn ti-btn-danger" data-act="no_go"${_st.busy ? ' disabled' : ''}>Отказ от участия</button>
        </div>
      </div>`;
    } else {
      form = `<p class="ti-hint">Решение принимается в статусах «${STATUS_LABEL.screened}» и «${STATUS_LABEL.decision}», сейчас «${esc(STATUS_LABEL[t.status] ?? t.status)}».</p>`;
    }
  } else {
    form = '<p class="ti-hint">Решение принимает Марат или владелец. Здесь только просмотр.</p>';
  }
  return `${summary}<h4 class="tdi-h">Решение</h4>${info}${form}${renderPriceSection(t)}`;
}

// ── Этапы ──

// Э-1 / ТЕНД-ДИНАР-3: кнопки ведения этапов Динара по статусу карточки.
const STAGE_ACTION_LABEL = {
  submitted: 'Заявка подана', result: 'Итог торгов',
  guarantee_done: 'Гарантия оформлена', contract_signed: 'Контракт подписан',
  to_control: 'На контроль', back_to_submitted: 'Вернуть в «Подана»',
  customer_cancel: 'Отменён заказчиком',
};
// У статуса бывает несколько действий. «Богатое» действие (с полями ввода и/или комментарием —
// STAGE_NOTE_ACTIONS) всегда одно на статус; остальные — простые кнопки без полей.
const STAGE_ACTIONS_BY_STATUS = {
  approved:  ['customer_cancel'],
  docs:      ['submitted', 'customer_cancel'],
  submitted: ['result', 'to_control', 'customer_cancel'],
  control:   ['result', 'back_to_submitted', 'customer_cancel'],
  won:       ['guarantee_done'],
  guarantee: ['contract_signed'],
};
// Действия с формой (поля и/или комментарий); остальные рендерятся простой кнопкой.
const STAGE_NOTE_ACTIONS = new Set(['submitted', 'result', 'guarantee_done', 'contract_signed']);
const OUTCOME_LABEL = { won: 'Выиграли', lost: 'Проиграли', customer_refused: 'Заказчик отказал' };
// ТЕНД-ЭТАПЫ-ФИКС: кнопки видят все, у кого есть доступ к аукционам (право tender.read);
// владелец — всегда. Бэкенд проверяет то же право (tender.read; владелец — всегда).
const canManageStages = () => canDo('tender.read');

function renderStages(t) {
  const idx = flowIndex(t.status);
  const byKey = Object.fromEntries((t.stages ?? []).map(s => [s.stage, s]));
  const failed = ['lost', 'customer_refused'].includes(t.status);
  const rows = STAGES.map(s => {
    const rec = byKey[s.key];
    const done = rec?.done_at || (idx >= 0 && idx >= flowIndex(s.doneAt));
    const planned = rec?.planned_at ?? (s.date ? t[s.date] : null);
    let state = done ? 'done' : 'todo';
    if (failed && s.key === 'auction') state = 'failed';
    return `<tr class="tdi-stage tdi-stage--${state}">
      <td>${state === 'done' ? '✓' : state === 'failed' ? '✕' : '○'}</td>
      <td>${esc(s.label)}</td>
      <td>${fmtDate(planned, true)}</td>
      <td>${rec?.done_at ? fmtDate(rec.done_at, true) : ''}</td>
      <td class="ti-hint-sm">${esc(rec?.note ?? '')}</td></tr>`;
  }).join('');
  const facts = [
    t.application_number ? field('№ заявки', esc(t.application_number)) : '',
    t.final_price != null ? field('Итоговая цена', fmtMoney(t.final_price)) : '',
    t.contract_due ? field('Срок подписания контракта', fmtDate(t.contract_due)) : '',
  ].filter(Boolean).join('');
  const project = t.project_id
    ? `<p>Проект создан: <a href="/gantt/${t.project_id}" data-route="/gantt/${t.project_id}" class="tdi-link" data-project-link>открыть в Gantt (#${t.project_id})</a></p>` : '';
  return `
    <ol class="tdi-steps">${['new', 'screened', 'decision', 'approved', 'docs', 'submitted', 'won', 'guarantee', 'contract'].map((s, i) =>
      `<li class="${i < idx ? 'is-done' : i === idx ? 'is-current' : ''}">${esc(STATUS_LABEL[s])}</li>`).join('')}</ol>
    ${idx < 0 && t.is_closed ? `<p class="ti-hint">Карточка закрыта: ${esc(statusText(t))}.</p>` : ''}
    ${renderStageActions(t)}
    <table class="tdi-table"><thead><tr><th></th><th>Этап</th><th>План</th><th>Факт</th><th></th></tr></thead>
      <tbody>${rows}</tbody></table>
    ${facts ? `<div class="tdi-stage-facts">${facts}</div>` : ''}
    ${project}
    <p class="ti-hint-sm">Этапы ведёт Динар кнопками здесь. Контракт создаёт проект в очереди CCPM.</p>`;
}

// Э-1 / ТЕНД-ДИНАР-3: несколько кнопок этапов по статусу (видны всем с доступом к аукционам).
// Богатое действие (одно на статус) рисуется формой с полями/комментарием; простые действия —
// отдельными кнопками (так не плодятся дубли id tdi-stage-note и единый черновик _st.stage).
function renderStageActions(t) {
  const actions = STAGE_ACTIONS_BY_STATUS[t.status] ?? [];
  if (!actions.length) return '';
  if (!canManageStages()) {
    return '<p class="ti-hint-sm">Этапы ведёт любой, у кого есть доступ к аукционам.</p>';
  }
  const rich = actions.find(a => STAGE_NOTE_ACTIONS.has(a));  // максимум одно на статус
  const simple = actions.filter(a => !STAGE_NOTE_ACTIONS.has(a));
  const hint = t.status === 'control'
    ? '<p class="ti-hint-sm">На контроле: следим за спорами, при случае подаём заявку заново или отзываем.</p>' : '';
  const richHtml = rich ? renderStageForm(t, rich) : '';
  const simpleHtml = simple.length
    ? `<div class="tdi-decide-btns">${simple.map(stageButton).join('')}</div>` : '';
  return `${hint}${richHtml}${simpleHtml}`;
}

// Простая кнопка действия (без полей): «На контроль», «Вернуть в «Подана»», «Отменён заказчиком».
function stageButton(action) {
  const danger = action === 'customer_cancel';
  return `<button type="button" class="ti-btn${danger ? ' ti-btn-danger' : ''}"
    data-act="stage" data-stage-act="${action}"${_st.busy ? ' disabled' : ''}>${esc(STAGE_ACTION_LABEL[action])}</button>`;
}

// Форма богатого действия: поля (у submitted/result) + комментарий + кнопка.
function renderStageForm(t, action) {
  const s = _st.stage;
  const note = `<div class="ti-field"><label class="ti-label" for="tdi-stage-note">Комментарий</label>
    <textarea id="tdi-stage-note" rows="2" class="ti-input" data-stage-note placeholder="по желанию">${esc(s.note)}</textarea></div>`;
  const btn = `<div class="tdi-decide-btns"><button type="button" class="ti-btn ti-btn-primary"
    data-act="stage" data-stage-act="${action}"${_st.busy ? ' disabled' : ''}>${esc(STAGE_ACTION_LABEL[action])}</button></div>`;
  let fields = '';
  if (action === 'submitted') {
    fields = `<div class="ti-field"><label class="ti-label" for="tdi-stage-appno">№ заявки</label>
      <input id="tdi-stage-appno" class="ti-input" data-stage-appno value="${esc(s.application_number)}" placeholder="по желанию"></div>`;
  } else if (action === 'result') {
    const opt = (c) => `<option value="${c}"${s.outcome === c ? ' selected' : ''}>${esc(OUTCOME_LABEL[c])}</option>`;
    fields = `
      <div class="ti-field"><label class="ti-label" for="tdi-stage-outcome">Результат торгов</label>
        <select id="tdi-stage-outcome" class="ti-input" data-stage-outcome>${Object.keys(OUTCOME_LABEL).map(opt).join('')}</select></div>
      <div class="ti-field"><label class="ti-label" for="tdi-stage-finalprice">Итоговая цена, ₽</label>
        <input id="tdi-stage-finalprice" type="number" min="0" step="any" class="ti-input" data-stage-finalprice value="${esc(s.final_price)}" placeholder="по желанию"></div>
      <div class="ti-field" data-stage-due-field${s.outcome === 'won' ? '' : ' hidden'}>
        <label class="ti-label" for="tdi-stage-contractdue">Срок подписания контракта</label>
        <input id="tdi-stage-contractdue" type="date" class="ti-input" data-stage-contractdue value="${esc(s.contract_due)}">
        <p class="ti-hint-sm">Обязателен при «Выиграли».</p></div>`;
  }
  return `<div class="tdi-stage-form" data-stage-form="${action}">
    <h4 class="tdi-h">${esc(STAGE_ACTION_LABEL[action])}</h4>${fields}${note}${btn}</div>`;
}

// ── Подвал: переходы по словарю ──

function renderFooter(t) {
  if (!canDo('tender.transition')) return '';
  const role = getCurrentUser()?.role;
  const targets = [...(TRANSITIONS[t.status] ?? [])]
    .filter(s => !DECISION_TARGETS.has(s) && manualAllowed(t.status, s) && roleAllows(t.status, s, role));
  const btns = targets.map(s => `<button type="button" class="ti-btn ${s === 'cancelled' ? 'ti-btn-danger' : ''}"
    data-transition="${s}"${_st.busy ? ' disabled' : ''}>${s === 'cancelled' ? 'Отменить' : `→ ${esc(STATUS_LABEL[s])}`}</button>`);
  if (DECIDE_FROM.has(t.status)) btns.unshift('<span class="ti-hint-sm">Решение — во вкладке «Решение»</span>');
  if (isPrefilterRejected(t)) {
    btns.unshift('<span class="ti-hint-sm">Отсеяна при первичном отборе. Вернуть — во вкладке «Данные», блок «Отбор».</span>');
  }
  if (t.status === 'expired') {
    btns.unshift(`<span class="ti-hint-sm">Закрыта автоматически: ${esc(statusText(t))}.${role === 'owner' ? '' : ' Вернуть в работу может владелец.'}</span>`);
  }
  return btns.join('');
}

// ── События ──

async function transition(target) {
  const t = _st.tender;
  if (target === 'cancelled') {
    const ok = await openModal({ title: 'Отменить аукцион', message: `${cardName(t)} «${t.title ?? ''}» будет закрыт.`, confirmLabel: 'Отменить аукцион', danger: true });
    if (!ok) return;
  }
  if (target === 'contract' && !t.project_id) {
    const ok = await openModal({ title: 'Контракт', message: 'Будет создан проект в очереди CCPM (queued).', confirmLabel: 'Создать проект' });
    if (!ok) return;
  }
  await run(() => api.tenders.transition(t.id, { status: target }), `${cardName(t)}: ${STATUS_LABEL[t.status]} → ${STATUS_LABEL[target]}`);
  if (target === 'contract' && _st.tender?.project_id) _st.tab = 'stages';
  render();
}

// Э-1 / ТЕНД-ДИНАР-3: Динар отмечает этап кнопкой в карточке.
async function stageAction(action) {
  const t = _st.tender;
  const s = _st.stage;
  const body = { action };
  // Комментарий — только у действий с формой (у простых кнопок поля нет, черновик не трогаем).
  if (STAGE_NOTE_ACTIONS.has(action)) {
    const note = (s.note || '').trim();
    if (note) body.note = note;
  }
  if (action === 'submitted') {
    const no = (s.application_number || '').trim();
    if (no) body.application_number = no;
  } else if (action === 'result') {
    body.outcome = s.outcome || 'won';
    const fp = String(s.final_price ?? '').trim();
    if (fp !== '') {
      const n = Number(fp);
      if (!Number.isFinite(n) || n <= 0) { _st.error = 'Итоговая цена должна быть больше нуля'; render(); return; }
      body.final_price = n;
    }
    if (body.outcome === 'won') {
      if (!s.contract_due) { _st.error = 'Укажите срок подписания контракта'; render(); return; }
      body.contract_due = s.contract_due;
    }
  }
  if (action === 'result' && body.outcome !== 'won') {
    const ok = await openModal({
      title: 'Итог торгов', danger: true, confirmLabel: OUTCOME_LABEL[body.outcome],
      message: `${cardName(t)}: ${OUTCOME_LABEL[body.outcome]}. Карточка закроется.`,
    });
    if (!ok) return;
  }
  if (action === 'customer_cancel') {
    const ok = await openModal({
      title: 'Отменён заказчиком', danger: true, confirmLabel: 'Отметить «Отменён заказчиком»',
      message: `${cardName(t)}: отметить «Отменён заказчиком»? Карточка будет закрыта.`,
    });
    if (!ok) return;
  }
  const res = await run(() => api.tenders.stageAction(t.id, body), `${cardName(t)}: ${STAGE_ACTION_LABEL[action]}`);
  if (res) { _st.stage = blankStage(); if (_st.tender?.project_id) _st.tab = 'stages'; render(); }
}

async function onClick(e) {
  const el = e.target instanceof Element ? e.target : null;
  if (!el || !_st.tender) return;
  const tab = el.closest('[data-tab]');
  if (tab) { _st.tab = tab.dataset.tab; _st.error = null; render(); return; }
  const tr = el.closest('[data-transition]');
  if (tr) { await transition(tr.dataset.transition); return; }
  const del = el.closest('[data-doc-del]');
  if (del) { await deleteDocument(Number(del.dataset.docDel)); return; }
  const open = el.closest('[data-doc-open]');
  if (open) { openDocViewer(_st.tender, Number(open.dataset.docOpen)); return; }
  const actEl = el.closest('[data-act]');
  const act = actEl?.dataset.act;
  const id = _st.tender.id;
  if (act === 'upload') { _root.querySelector('[data-upload-input]').click(); return; }
  if (act === 'upload-as') { await uploadFiles(_st.upload.pending ?? [], actEl.dataset.kind); return; }
  if (act === 'pf_pass' || act === 'pf_reject') {
    await prefilterOverride(act === 'pf_pass' ? 'pass' : 'reject');
  } else if (act === 'enrich') {
    await run(() => api.tenders.enrichRequest(id), 'Обогащение запрошено: worker возьмёт карточку первой');
  } else if (act === 'estimate') {
    const body = estimatePayload();
    if (!body.items.length) { _st.error = 'Укажите объём хотя бы одной позиции'; render(); return; }
    const res = await run(() => api.tenders.estimate(id, body), 'Расчёт обновлён');
    if (res && res !== true) { _st.estimate = res; _st.qty = {}; render(); }
  } else if (act === 'screen') {
    await screen(id);
  } else if (act === 'save_price') {
    await savePrice();
  } else if (act === 'go' || act === 'no_go') {
    await decide(act);
  } else if (act === 'stage') {
    await stageAction(actEl.dataset.stageAct);
  } else if (act === 'link_customer') {
    await linkCustomer();
  } else if (act === 'unlink_customer') {
    await run(() => api.tenders.patch(id, { customer_id: null }), 'Связь со справочником убрана');
  }
}

/** Выбрать заказчика аукциона из справочника (СПР-1b); текст из закупки не затираем. */
async function linkCustomer() {
  const t = _st.tender;
  const res = await pickCounterparty({
    role: 'customer', title: 'Заказчик аукциона', currentId: t.customer_id ?? null,
  });
  if (!res) return;
  if (res.action === 'unlink') {
    await run(() => api.tenders.patch(t.id, { customer_id: null }), 'Связь со справочником убрана');
  } else if (res.action === 'select') {
    const body = { customer_id: res.id };
    if (!t.customer_name) body.customer_name = res.name;  // имя из закупки приоритетнее
    await run(() => api.tenders.patch(t.id, body), 'Заказчик связан со справочником');
  }
}

// М-1: решение с категорией и комментарием; правило обязательности — как у POST /decision.
async function decide(decision) {
  const t = _st.tender;
  const d = _st.decide;
  const comment = d.comment.trim();
  const body = { decision };
  let dev = null;
  let priceConfirm = '';
  if (decision === 'go') {
    // Ц-0: цену вводит человек; пусто/≤0 — ошибка, запроса нет; подозрение на порядок
    // цифр — подтверждение «Ты уверен?».
    const chk = checkPrice1(d.price, { recommended: t.recommended_price, nmck: t.initial_price });
    if (chk.error) { _st.error = chk.error; render(); return; }
    body.price_1 = chk.price;
    priceConfirm = chk.confirm;
    dev = deviationPct(chk.price, t.recommended_price);
  }
  const err = validateDecision(decision, { dev, recommendation: t.recommendation, category: d.category, comment });
  if (err) { _st.error = err; render(); return; }
  if (d.category) body.reason_category = d.category;
  if (comment) body.comment = comment;
  // eslint-disable-next-line no-alert
  if (priceConfirm && !window.confirm(priceConfirm)) return;
  if (decision === 'no_go') {
    const ok = await openModal({
      title: 'Отказ от участия', danger: true, confirmLabel: 'Отказаться',
      message: `${cardName(t)}: ${reasonLabel(d.category)}. ${comment}`,
    });
    if (!ok) return;
  }
  const res = await run(() => api.tenders.decision(t.id, body), decision === 'go' ? 'Решение: участвуем' : 'Решение: отказ');
  if (res) {
    // Ц-0: после успешной отправки черновик сбрасывается.
    _st.decide = { price: '', category: '', comment: '' };
    _draft = null;
    _st.precedents = null;
    render();
  }
}

// Ц-1: смена цены участия после решения. Проверка цены — та же, что в Ц-0 (checkPrice1);
// при отклонении больше 10 % от рекомендованной нужна категория причины (как в /decision).
async function savePrice() {
  const t = _st.tender;
  const d = _st.priceEdit;
  const chk = checkPrice1(d.price, { recommended: t.recommended_price, nmck: t.initial_price });
  if (chk.error) { _st.error = chk.error; render(); return; }
  const dev = deviationPct(chk.price, t.recommended_price);
  if (dev != null && Math.abs(dev) > AGREE_DEVIATION_PCT && !d.category) {
    _st.error = `Укажите категорию причины: цена участия отличается от рекомендованной на ${fmtDeviation(dev)}`;
    render();
    return;
  }
  if (d.category && !GO_REASONS.includes(d.category)) {
    _st.error = `Категория «${REASON_LABEL[d.category]}» не подходит к смене цены`;
    render();
    return;
  }
  const body = { price_1: chk.price };
  const comment = d.comment.trim();
  if (comment) body.comment = comment;
  if (d.category) body.reason_category = d.category;
  // eslint-disable-next-line no-alert
  if (chk.confirm && !window.confirm(chk.confirm)) return;
  const res = await run(() => api.tenders.price(t.id, body), 'Цена участия обновлена');
  if (res) {
    _st.priceEdit = { price: '', category: '', comment: '' };
    render();
  }
}

async function screen(id) {
  if (_st.busy) return;
  _st.busy = true;
  _st.error = null;
  render();
  try {
    const res = await api.tenders.screen(id);
    _st.notice.screening = res?.screening_json || res?.status === 'screened'
      ? 'Раскладка готова.'
      : 'В очереди: раскладка обновится после ближайшего прогона.';
    await reload();
    _st.onChange(_st.tender);
  } catch (e) {
    if (e.status === 501) _st.notice.screening = 'Раскладка появится после сессии A2 (движок ещё не выкачен).';
    else if (e.message !== '401') _st.error = apiErrorText(e);
  } finally {
    _st.busy = false;
    render();
  }
}

function onInput(e) {
  const el = e.target;
  if (el.matches('[data-qty]')) _st.qty[el.dataset.qty] = el.value;
  else if (el.matches('[data-contract-price]')) _st.contractPrice = el.value;
  else if (el.matches('[data-comment]')) _st.decide.comment = el.value;
  else if (el.matches('[data-price1]')) {
    // Отклонение, подсказку и живую сумму обновляем на месте: полный render сбросил бы фокус поля.
    _st.decide.price = el.value;
    const t = _st.tender;
    const devEl = _root.querySelector('[data-deviation]');
    if (devEl) devEl.textContent = deviationText(t);
    const hint = _root.querySelector('[data-reason-hint]');
    if (hint) hint.textContent = decideHint(t);
    const sumEl = _root.querySelector('[data-price1-sum]');
    if (sumEl) sumEl.textContent = priceSumText(el.value);
  } else if (el.matches('[data-stage-appno]')) {
    _st.stage.application_number = el.value;
  } else if (el.matches('[data-stage-finalprice]')) {
    _st.stage.final_price = el.value;
  } else if (el.matches('[data-stage-contractdue]')) {
    _st.stage.contract_due = el.value;
  } else if (el.matches('[data-stage-note]')) {
    _st.stage.note = el.value;
  } else if (el.matches('[data-price-comment]')) {
    _st.priceEdit.comment = el.value;
  } else if (el.matches('[data-price-input]')) {
    // Ц-1: как и в форме решения — живое обновление без полного render (чтобы не терять фокус).
    _st.priceEdit.price = el.value;
    const t = _st.tender;
    const devEl = _root.querySelector('[data-price-deviation]');
    if (devEl) devEl.textContent = priceDeviationText(t);
    const hint = _root.querySelector('[data-price-hint]');
    if (hint) hint.textContent = priceHint(t);
    const sumEl = _root.querySelector('[data-price-sum]');
    if (sumEl) sumEl.textContent = priceSumText(el.value);
  }
}

function onChangeEvt(e) {
  const el = e.target;
  if (el.matches('[data-upload-input]')) {
    const files = [...(el.files ?? [])];
    el.value = '';
    uploadFiles(files);
    return;
  }
  if (el.matches('[data-flag]')) {
    const f = el.dataset.flag;
    _st.flags = el.checked ? [..._st.flags, f] : _st.flags.filter(x => x !== f);
    return;
  }
  if (el.matches('[data-object-kind]')) { _st.objectKind = el.value; render(); }
  if (el.matches('[data-stage-outcome]')) {
    // Переключение итога показывает/прячет срок контракта (обязателен при «Выиграли»).
    _st.stage.outcome = el.value;
    const dueField = _root.querySelector('[data-stage-due-field]');
    if (dueField) dueField.hidden = el.value !== 'won';
  }
  if (el.matches('[data-reason]')) _st.decide.category = el.value;
  if (el.matches('[data-price-reason]')) {
    _st.priceEdit.category = el.value;
    const hint = _root.querySelector('[data-price-hint]');
    if (hint) hint.textContent = priceHint(_st.tender);
  }
}
